import {
  assembleAnswers,
  changedRecord,
  corrections,
  type HouseholdFactInsert,
  type HouseholdFactRow,
  type PersonFactInsert,
  type PersonFactRow,
  type PersonRow,
} from '@asvise/household-engine/record'
import type { Answers, Assessment, Person, PersonId } from '@asvise/household-engine/vocabulary'
import { supabase } from './supabase'

/**
 * Reading and writing a household record.
 *
 * The same shape as the business app's store and the same two rules. Facts are
 * appended and never revised, so a write is a diff rather than a document. And
 * the winner among several accounts of one field is decided here, by the same
 * `preferFact` the write side uses, rather than by a query ordering that could
 * disagree with it.
 *
 * What is different is that there are three things to read rather than two:
 * the household's own answers, its members, and each member's answers. They are
 * fetched together because they are one question, and assembled by the engine's
 * `assembleAnswers`, which is the only code that knows a household has members
 * at all.
 */

export interface LoadedRecord {
  householdId: string
  answers: Answers
  /** The raw rows, handed back with the answers because both come from one read. */
  householdFacts: HouseholdFactRow[]
  personFacts: PersonFactRow[]
}

const db = () => {
  if (!supabase) throw new Error('This deployment has no database configured.')
  return supabase
}

/** The household this user owns, or null where they have none yet. */
export async function getHousehold(
  userId: string,
): Promise<{ id: string; name: string } | null> {
  const { data, error } = await db()
    .from('households')
    .select('id, name')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
    .limit(1)
  if (error) throw error
  const row = data?.[0]
  return row ? { id: row.id as string, name: row.name as string } : null
}

/**
 * The household, created if this is the first visit.
 *
 * `user_id` is deliberately not sent. The column defaults to `auth.uid()`, so
 * the one place it could be got wrong is the one place it is not written.
 */
export async function currentHousehold(): Promise<{ id: string; name: string }> {
  const { data: session } = await db().auth.getUser()
  const userId = session.user?.id
  if (!userId) throw new Error('Nobody is signed in.')

  const existing = await getHousehold(userId)
  if (existing) return existing

  const { data, error } = await db()
    .from('households')
    .insert({})
    .select('id, name')
    .single()
  if (error) throw error
  return { id: data.id as string, name: data.name as string }
}

/**
 * Everything a household record is made of, in one read.
 *
 * Four queries rather than one join, because the shapes are different and
 * PostgREST would return the facts multiplied by the people. They run together,
 * so a record cannot be assembled from a household read at one moment and
 * members read at another.
 */
export async function loadRecord(userId: string): Promise<LoadedRecord | null> {
  const household = await getHousehold(userId)
  if (!household) return null

  const [people, householdFacts, personFacts, policies] = await Promise.all([
    db().from('people').select('*').eq('household_id', household.id),
    db().from('household_facts').select('*').eq('household_id', household.id),
    db().from('person_facts').select('*').eq('household_id', household.id),
    db().from('policies').select('id, document').eq('household_id', household.id),
  ])

  for (const r of [people, householdFacts, personFacts, policies]) {
    if (r.error) throw r.error
  }

  const answers = assembleAnswers({
    household_name: household.name,
    people: (people.data ?? []) as PersonRow[],
    household_rows: (householdFacts.data ?? []) as HouseholdFactRow[],
    person_rows: (personFacts.data ?? []) as PersonFactRow[],
    policies: (policies.data ?? []).map((p) => p.document as Answers['policies'][number]),
  })

  return {
    householdId: household.id,
    answers,
    householdFacts: (householdFacts.data ?? []) as HouseholdFactRow[],
    personFacts: (personFacts.data ?? []) as PersonFactRow[],
  }
}

/**
 * Members, written whole rather than diffed.
 *
 * A person is identity, not an observation: they are added, renamed or they
 * leave, and none of those is a new version of a previous answer. So this is an
 * upsert and a delete of what is no longer listed, which is how the business app
 * writes policies and for the same reason.
 *
 * Their facts are not touched here. Deleting a member cascades their facts away,
 * which is the one place where leaving does destroy something, and it is the
 * right thing: a record of somebody who is no longer part of this household is
 * data nobody asked us to keep.
 */
export async function savePeople(householdId: string, people: Person[]): Promise<void> {
  const rows = people.map((p, position) => ({
    id: p.id,
    household_id: householdId,
    label: p.label,
    role: p.role,
    position,
  }))

  if (rows.length > 0) {
    const { error } = await db().from('people').upsert(rows)
    if (error) throw error
  }

  const keep = people.map((p) => p.id)
  const gone = db().from('people').delete().eq('household_id', householdId)
  const { error } = keep.length > 0 ? await gone.not('id', 'in', `(${keep.join(',')})`) : await gone
  if (error) throw error
}

/**
 * The rows a change appends, written and handed back.
 *
 * Only what the household itself said. The client is the author of USER facts
 * and nothing else: a document or connector fact is written by an endpoint that
 * holds the credential behind it, and a browser claiming to be one is claiming a
 * rank it has not earned.
 *
 * Unlike the business app, this is not only a convention here. The database
 * refuses it: the insert policy on both fact tables carries
 * `with check (… and source = 'USER')`, so the filter below is what keeps the
 * request from being rejected rather than what keeps it honest.
 */
export async function saveRecord(input: {
  householdId: string
  previous: Answers | null
  current: Answers
  /** Fields still being typed, withheld so a half-entered figure is not recorded. */
  withholding?: Set<string>
}): Promise<{ household: HouseholdFactInsert[]; person: PersonFactInsert[] }> {
  const { data: session } = await db().auth.getUser()
  const userId = session.user?.id
  if (!userId) throw new Error('Nobody is signed in.')

  await savePeople(input.householdId, input.current.people)
  await nameIt(input.householdId, input.current.household_name, input.previous?.household_name)

  const changed = changedRecord(input.previous, input.current, {
    household_id: input.householdId,
    user_id: userId,
  })

  const held = input.withholding ?? new Set<string>()
  const household = changed.household.filter((r) => r.source === 'USER' && !held.has(r.field))
  const person = changed.person.filter(
    (r) => r.source === 'USER' && !held.has(`${r.person_id}:${r.field}`),
  )

  /**
   * `ignoreDuplicates` is required rather than tidy.
   *
   * A merging upsert compiles to `on conflict do update`, which needs UPDATE
   * privilege on the table. That privilege deliberately does not exist: the
   * grant is select and insert, so append-only holds even if a policy were
   * written wrongly. Without this flag the write fails on the second save of an
   * unchanged answer within one editing session.
   */
  if (household.length > 0) {
    const { error } = await db()
      .from('household_facts')
      .upsert(household, {
        onConflict: 'household_id,field,observed_at,source',
        ignoreDuplicates: true,
      })
    if (error) throw error
  }

  if (person.length > 0) {
    const { error } = await db()
      .from('person_facts')
      .upsert(person, {
        onConflict: 'person_id,field,observed_at,source',
        ignoreDuplicates: true,
      })
    if (error) throw error
  }

  await touch(input.householdId)
  return { household, person }
}

/**
 * What the household calls itself, which is a label rather than a fact.
 *
 * Not appended like an answer and not aged: nobody needs to know that a
 * household used to be called something else, and a rename is a correction to
 * a label rather than a new observation about a family. So it updates the row,
 * the way the business app treats a business name.
 *
 * It had no writer at all. The column defaults to "My household" and nothing
 * in the app set it or showed it, so every household was called that and the
 * board had no name to put at the top.
 */
async function nameIt(householdId: string, next: string, before?: string): Promise<void> {
  const label = next.trim()
  if (label === '' || label === before?.trim()) return
  const { error } = await db().from('households').update({ name: label }).eq('id', householdId)
  if (error) throw error
}

/**
 * What a retention period would count from.
 *
 * Maintained although nothing reads it yet, and deliberately so: the day a
 * period is set, this column is what it measures against, and a column that
 * starts being written on that day would read as every household having last
 * visited that morning. Keeping it current costs one update per save and is the
 * difference between setting a period and waiting out a period before setting
 * one.
 *
 * Failing quietly is deliberate too: a household that cannot write this has
 * still saved its answers, and losing a screening because a timestamp would not
 * move is the wrong trade.
 */
async function touch(householdId: string): Promise<void> {
  await db()
    .from('households')
    .update({ last_active_at: new Date().toISOString() })
    .eq('id', householdId)
}

/** Policies, written whole. A schedule is a document with parts, not a value with an age. */
export async function savePolicies(
  householdId: string,
  policies: Answers['policies'],
): Promise<void> {
  const rows = policies.map((p) => ({
    id: p.id,
    household_id: householdId,
    insurer: p.insurer,
    product_name: p.product_name,
    policy_number: p.policy_number,
    source: p.source,
    document: p,
  }))

  if (rows.length > 0) {
    const { error } = await db().from('policies').upsert(rows)
    if (error) throw error
  }

  const keep = policies.map((p) => p.id)
  const gone = db().from('policies').delete().eq('household_id', householdId)
  const { error } = keep.length > 0
    ? await gone.not('id', 'in', `(${keep.map((id) => `"${id}"`).join(',')})`)
    : await gone
  if (error) throw error
}

export type { PersonId }

/**
 * A screening, kept.
 *
 * Framework section 10.3: every result stores sixteen things. This table had no
 * writer before now, so an assessment was computed, shown, and gone when the
 * tab closed, which made the privacy policy's undertaking to say within thirty
 * days how a household's data was used impossible to honour from anything but
 * memory.
 *
 * Both the answers and the assessment, deliberately, and the original migration
 * explains why: `assess` depends on when it ran because facts expire, so
 * replaying stored answers later is not faithful to what somebody was shown.
 *
 * Failing is loud here, unlike the `last_active_at` touch above. A household
 * that was shown a screening nobody recorded has a different kind of problem
 * from one whose timestamp did not move, and swallowing it would mean the
 * audit record's absence is the one thing the audit record cannot report.
 */
export async function saveAssessment(input: {
  householdId: string
  householdName: string
  answers: Answers
  assessment: Assessment
  explanationVersion: string
}): Promise<string> {
  const policies = input.answers.policies
  const findings = input.assessment.findings

  /**
   * The fact rows, read here rather than passed in.
   *
   * They were a parameter first, taken from the copy the first load returned,
   * and that copy is already out of date by the time anybody asks for a
   * screening: every answer typed since has appended rows the page never saw.
   * A correction made in this session would have been missing from the audit
   * record of the assessment it changed, which is the one assessment it
   * matters for.
   */
  const [householdFacts, personFacts] = await Promise.all([
    db().from('household_facts').select('*').eq('household_id', input.householdId),
    db().from('person_facts').select('*').eq('household_id', input.householdId),
  ])
  for (const r of [householdFacts, personFacts]) if (r.error) throw r.error

  const { data, error } = await db()
    .from('assessments')
    .insert({
      household_id: input.householdId,
      household_name: input.householdName,
      answers: input.answers,
      assessment: input.assessment,
      rule_set_version: input.assessment.rule_set_version,
      country: input.assessment.country,
      country_module_version: input.assessment.country_module_version,
      profile_schema_version: input.assessment.profile_schema_version,
      explanation_version: input.explanationVersion,
      extraction_versions: [
        ...new Set(policies.map((p) => p.read_by_version).filter((v): v is string => Boolean(v))),
      ],
      /**
       * Lifted out of the snapshot rather than computed from it later, because
       * an access request should be one query. The same values are inside the
       * document; these are the copies with an index on them.
       */
      rule_ids: [...new Set(findings.flatMap((f) => f.evidence.map((e) => e.rule_id)))],
      reason_codes: [...new Set(findings.flatMap((f) => f.reason_codes))],
      human_review_state: reviewStateOf(policies),
      user_corrections: corrections(
        (householdFacts.data ?? []) as HouseholdFactRow[],
        (personFacts.data ?? []) as PersonFactRow[],
      ),
    })
    .select('id')
    .single()

  if (error) throw error
  return (data as { id: string }).id
}

/**
 * Whether anybody has checked what was read out of the documents.
 *
 * MARKED outranks the rest because it is the only one that changes what the
 * engine will claim: a policy the document-quality gate found something
 * against cannot produce a figure the comparison treats as the cover
 * available, and that is true however carefully a person confirmed the lines
 * that were read.
 */
function reviewStateOf(policies: Answers['policies']): string {
  if (policies.length === 0) return 'NONE'
  if (policies.some((p) => (p.review_required ?? []).length > 0)) return 'MARKED'
  const confirmed = policies.filter((p) => Boolean(p.confirmed_at)).length
  if (confirmed === policies.length) return 'CONFIRMED'
  if (confirmed === 0) return 'NONE'
  return 'PARTLY_CONFIRMED'
}
